/**
 * Date    : 2022/1/12
 * Author  : WeiLin
 * Declare : index
 */

import React, { useEffect, useImperativeHandle, useMemo, useState, useRef } from 'react';
import { useCounter, useMount, useUnmount } from 'ahooks';

import { message } from '@/components/AntD';
import { blobToBase64 } from '@/components/FileUpload/FileUtils';
import { FILE_ACCEPT } from '@/constants/common';
import { EventEmitter, EVENT_NAMES } from '@/utils/eventEmitter';
import OBSUploader from '@/components/FileUpload/OBSUpload';
import uuid from '@/utils/uuid';

export default function FilePiker(props) {

  const { maxCount = 3, className, fileRef, needConvert, children, initialValue, onBefore, onChange, onError, options, type, pikerText, sizeLimit } = props;

  // file element
  const filePiker = useRef(null);

  // drop element
  const pikerDrop = useRef(null);

  // event
  const _eventEmitter = useMemo(() => {
    return new EventEmitter();
  }, []);

  const [files, setFiles] = useState([]);
  const [uploadTimes, { inc: addUploadTimes }] = useCounter(0);

  useEffect(() => {
    setFiles(initFileValue(initialValue));
  }, [initialValue]);

  // export 方法
  useImperativeHandle(fileRef, () => ({
    filePiker: filePiker.current, // return element
    addFile,
    selectFile
  }));

  /**state  state部分**/
  const acceptValue = useMemo(() => {
    const { accept } = FILE_ACCEPT(needConvert)[type] || {};
    return accept || '';
  }, [type]);

  /**effect  effect部分**/
  const mergeOptions = useMemo(() => {
    const FILE_CONFIG = {
      id: 'file',
      name: 'file',
      accept: acceptValue,
      maxFiles: '',
      required: false,
      multiple: false,
      disabled: false
    };
    return Object.assign(FILE_CONFIG, options);
  }, [options]);

  /**
   * 视图区
   * @returns {*}
   */
  function renderView() {
    const viewChild = children || <span className="cursor" onClick={addFile}>{ pikerText }</span>;
    return (
      <div className="file-pool--root">{ viewChild }</div>
    );
  }

  /**
   * 更新 state
   * @param values
   */
  function syncSetFiles(values) {
    setFiles((prevState) => {
      const [...newValues] = prevState;

      // 当选择多个图片一起上传时，要注意两个fileId一样
      const updateIndex = newValues.findIndex(item => item.fileId === values.fileId && (!values.fileName || item.fileName === values.fileName));

      if(newValues[updateIndex]) {
        const updateItem = newValues[updateIndex] || {};

        newValues[updateIndex] = Object.assign(updateItem, values);

        onChange && onChange(newValues);
      }

      return newValues;
    });
  }

  /**
   * add file
   */
  function addFile() {
    return new Promise((resolve, reject) => {
      if(!filePiker.current) return;
      filePiker.current.click();
      resolve(true);
    });
  }

  /**
   *  select files
   * @param e
   */
  function selectFile(e) {
    if(!e.target.files) return;
    let newFiles = [...e.target.files].slice(0, maxCount).map(item => {
      item.uuid = uuid();
      return item;
    });
    addUploadTimes();
    const accept = FILE_ACCEPT()[type]?.accept || '';
    if(accept && type && newFiles.findIndex(item => accept.split(', ').includes(item.type)) === -1) {
      onError && onError('仅支持上传' + FILE_ACCEPT()[type].name + '', newFiles);
      return;
    }

    for(let i = 0; i < newFiles.length; i++) {
      const size = newFiles[i].size;
      if(sizeLimit && sizeLimit < (size / 1024)) {
        return message.error(`上传的文件过大，上传的文件大小不能超过${sizeLimit > 1024 * 1024 ? (sizeLimit / (1024 * 1024)) + 'G' : (sizeLimit > 1024 ? (sizeLimit / 1024) + 'M' : sizeLimit + 'KB')}!`);
      }
    }

    if(onBefore && !onBefore(newFiles)) {
      return;
    }

    return new Promise((resolve, reject) => {

      // create file
      newFiles = initFileValue(newFiles);

      setFiles(newFiles);

      newFiles.forEach(item => {

        // 生成base64、上传图片
        blobToBase64(item.file, (value) => {
          syncSetFiles(Object.assign(item, { fileUrl: value }));
        });
        // 上传文件
        item.upload();
      });

      resolve(newFiles);

    });
  }

  // 初始化文件对象
  function initFileValue(values = []) {
    let [...files] = values;
    return files.map(file => {
      if(file instanceof Blob) {
        return new OBSUploader({
          file: file,
          config: {
            fail: (error) => {
              console.log(error, '上传失败');
            },
            progress: (values) => {
              syncSetFiles(values);
            },
            success: (values) => {
              syncSetFiles(values);
            }
          }
        });
      } else {
        return Object.assign({}, file);
      }
    });
  }

  /**
   * reset file
   */
  function resetFile() {
    setFiles([]);
  }

  useMount(() => {
    // add event
    _eventEmitter.on(`${EVENT_NAMES.FILE_EVENT}_ADD`, addFile);
    _eventEmitter.on(`${EVENT_NAMES.FILE_EVENT}_UPDATE`, syncSetFiles);
    _eventEmitter.on(`${EVENT_NAMES.FILE_EVENT}_SELECT`, selectFile);
    _eventEmitter.on(`${EVENT_NAMES.FILE_EVENT}_RESET`, resetFile);
  });

  useUnmount(() => {
    resetFile();
  });

  return (
    <div ref={pikerDrop} className={`FilePiker ${className}`}>
      { renderView() }
      <input key={uploadTimes} ref={filePiker} type="file" {...mergeOptions} style={{ display: 'none' }} onChange={selectFile} />
    </div>
  );
}

FilePiker.defaultProps = {
  className: '',
  action: '',
  options: {}, // maxFiles、required、multiple、disabled
  type: '', // 文件类型
  pikerText: '文件上传',
  onChange: null,
  /** 上传前回调 */
  onBefore: null,
  onError: null,
  allowDrop: false
};
